import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Monitor,
  Database,
  Image,
  Globe,
  Shield,
  Code,
  Keyboard,
  Plus,
  Eye,
  EyeOff,
  PenTool,
  ClipboardList,
  FileCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { 
  year12ICTCurriculum, 
  year12ICTYearlyPlanner, 
  year12ICTTests, 
  year12ICTWorksheets,
  year12ICTResources 
} from '@/data/year12ICTCurriculum';
import { year12ICTTestBlueprints, year12ICTUnitTests } from '@/data/year12ICTUnitTests';
import { year12ICTExercises, year12ICTAssignments } from '@/data/year12ICTExercisesAssignments';

export default function Year12ICTCurriculumPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedStrand, setSelectedStrand] = useState("strand1");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [expandedSLOs, setExpandedSLOs] = useState({});
  const [showAnswers, setShowAnswers] = useState({});

  const strandColors = {
    strand1: "blue",
    strand2: "green",
    strand3: "purple",
    strand4: "orange"
  };

  const strandIcons = {
    strand1: Monitor,
    strand2: Database,
    strand3: Image,
    strand4: Globe
  };

  const toggleSLOExpand = (subStrandId) => {
    setExpandedSLOs(prev => ({
      ...prev,
      [subStrandId]: !prev[subStrandId]
    }));
  };

  const toggleAnswers = (testId) => {
    setShowAnswers(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  // Navigate to Grade Book with test paper data
  const handleCreateAssessment = (test, term) => {
    const assessmentData = {
      fromCurriculum: true,
      title: test.title,
      subject: 'ICT',
      year: 'Year 12',
      term: term,
      totalMarks: test.totalMarks,
      duration: test.duration,
      instructions: test.instructions,
      sections: test.sections,
      testId: test.id
    };

    toast.success('Redirecting to Grade Book to create assessment...');
    navigate('/dashboard/gradebook', { state: { assessmentFromCurriculum: assessmentData } });
  };

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
          h1 { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .info { margin-bottom: 20px; }
          .instructions { background: #f5f5f5; padding: 15px; margin-bottom: 20px; border-radius: 5px; }
          .section { margin-bottom: 30px; }
          .section-title { background: #e0e0e0; padding: 10px; font-weight: bold; }
          .question { margin: 15px 0; padding: 10px; border-bottom: 1px dashed #ccc; }
          .options { margin-left: 20px; }
          .marks { color: #666; font-style: italic; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <div class="header">
          <div><strong>Name:</strong> _____________________</div>
          <div><strong>Date:</strong> _____________________</div>
        </div>
        <div class="info">
          <p><strong>Duration:</strong> ${test.duration} | <strong>Total Marks:</strong> ${test.totalMarks}</p>
        </div>
        <div class="instructions">
          <strong>Instructions:</strong>
          <ul>${test.instructions.map(i => `<li>${i}</li>`).join('')}</ul>
        </div>
    `;

    test.sections.forEach(section => {
      content += `
        <div class="section">
          <div class="section-title">${section.name} (${section.marks} marks)</div>
      `;
      section.questions.forEach(q => {
        content += `<div class="question">`;
        content += `<p><strong>Q${q.id}.</strong> ${q.question} <span class="marks">(${q.marks} mark${q.marks > 1 ? 's' : ''})</span></p>`;
        if (q.options) {
          content += `<div class="options">${q.options.map(o => `<p>${o}</p>`).join('')}</div>`;
        }
        content += `</div>`;
      });
      content += `</div>`;
    });

    content += `</body></html>`;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const currentStrand = year12ICTCurriculum.strands.find(s => s.id === selectedStrand);
  const termData = year12ICTYearlyPlanner[selectedTerm];
  const termTests = year12ICTTests[selectedTerm] || [];

  return (
    <div className="space-y-6" data-testid="year12-ict-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Year 12 ICT Curriculum</h1>
          <p className="text-gray-600 mt-1">
            Vanuatu National ICT Curriculum Framework - Complete SLOs, Planners, Assessments & Resources
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            4 Strands
          </Badge>
          <Badge variant="outline" className="text-green-600 border-green-600">
            11 Sub-strands
          </Badge>
          <Badge variant="outline" className="text-purple-600 border-purple-600">
            200+ SLOs
          </Badge>
        </div>
      </div>

      {/* Course Overview Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">{year12ICTCurriculum.courseOverview.totalWeeks}</p>
              <p className="text-sm text-gray-600">Total Weeks</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{year12ICTCurriculum.courseOverview.termsPerYear}</p>
              <p className="text-sm text-gray-600">Terms</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{year12ICTCurriculum.courseOverview.periodsPerWeek}</p>
              <p className="text-sm text-gray-600">Periods/Week</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{year12ICTCurriculum.courseOverview.totalPeriods}</p>
              <p className="text-sm text-gray-600">Total Periods</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">40%</p>
              <p className="text-sm text-gray-600">Practical Work</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 lg:w-auto lg:inline-flex">
          <TabsTrigger value="curriculum" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            <span className="hidden md:inline">SLOs</span>
          </TabsTrigger>
          <TabsTrigger value="planner" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden md:inline">Planner</span>
          </TabsTrigger>
          <TabsTrigger value="unit-tests" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden md:inline">Unit Tests</span>
          </TabsTrigger>
          <TabsTrigger value="exercises" className="flex items-center gap-2">
            <PenTool className="w-4 h-4" />
            <span className="hidden md:inline">Exercises</span>
          </TabsTrigger>
          <TabsTrigger value="assignments" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            <span className="hidden md:inline">Assignments</span>
          </TabsTrigger>
          <TabsTrigger value="tests" className="flex items-center gap-2">
            <FileCheck className="w-4 h-4" />
            <span className="hidden md:inline">Term Tests</span>
          </TabsTrigger>
          <TabsTrigger value="worksheets" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden md:inline">Worksheets</span>
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">Resources</span>
          </TabsTrigger>
        </TabsList>

        {/* Learning Outcomes Tab */}
        <TabsContent value="curriculum" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Strand Selector */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Strands</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {year12ICTCurriculum.strands.map(strand => {
                    const IconComponent = strandIcons[strand.id];
                    return (
                      <Button
                        key={strand.id}
                        variant={selectedStrand === strand.id ? "default" : "outline"}
                        className={`w-full justify-start text-left h-auto py-3 ${
                          selectedStrand === strand.id 
                            ? `bg-${strand.color}-600 hover:bg-${strand.color}-700` 
                            : ''
                        }`}
                        onClick={() => setSelectedStrand(strand.id)}
                        data-testid={`strand-btn-${strand.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <IconComponent className="w-5 h-5 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{strand.name}</p>
                            <p className="text-xs opacity-75 font-normal">
                              {strand.subStrands.length} sub-strands
                            </p>
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* SLOs Display */}
            <div className="lg:col-span-3">
              {currentStrand && (
                <Card>
                  <CardHeader className={`bg-${currentStrand.color}-50 border-b`}>
                    <CardTitle className={`text-${currentStrand.color}-800`}>
                      {currentStrand.name}
                    </CardTitle>
                    <CardDescription className="text-gray-700">
                      {currentStrand.majorOutcome}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ScrollArea className="h-[600px] pr-4">
                      <Accordion type="multiple" className="space-y-4">
                        {currentStrand.subStrands.map(subStrand => (
                          <AccordionItem 
                            key={subStrand.id} 
                            value={subStrand.id}
                            className="border rounded-lg overflow-hidden"
                          >
                            <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                              <div className="flex items-center gap-3 text-left">
                                <Badge variant="outline" className={`text-${currentStrand.color}-600 border-${currentStrand.color}-300`}>
                                  {subStrand.code}
                                </Badge>
                                <div>
                                  <p className="font-semibold">{subStrand.name}</p>
                                  <p className="text-sm text-gray-500">{subStrand.slos.length} SLOs</p>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <p className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded">
                                <strong>Key Learning Outcome:</strong> {subStrand.keyOutcome}
                              </p>
                              
                              {/* Group SLOs by Level */}
                              {[1, 2, 3, 4].map(level => {
                                const levelSLOs = subStrand.slos.filter(s => s.level === level);
                                if (levelSLOs.length === 0) return null;
                                
                                const levelColors = {
                                  1: 'bg-green-100 text-green-800',
                                  2: 'bg-blue-100 text-blue-800',
                                  3: 'bg-orange-100 text-orange-800',
                                  4: 'bg-red-100 text-red-800'
                                };
                                const levelLabels = {
                                  1: 'Basic (Recall & Identify)',
                                  2: 'Developing (Comprehension)',
                                  3: 'Proficient (Application)',
                                  4: 'Advanced (Analysis & Synthesis)'
                                };

                                return (
                                  <div key={level} className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge className={levelColors[level]}>
                                        Level {level}
                                      </Badge>
                                      <span className="text-xs text-gray-500">{levelLabels[level]}</span>
                                    </div>
                                    <div className="space-y-1 ml-4">
                                      {levelSLOs.map(slo => (
                                        <div 
                                          key={slo.code}
                                          className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-gray-50"
                                        >
                                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono shrink-0">
                                            {slo.code}
                                          </code>
                                          <span className="text-sm">{slo.description}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Yearly Planner Tab */}
        <TabsContent value="planner" className="mt-6">
          {/* Term Selector */}
          <div className="flex gap-2 mb-6">
            {Object.keys(year12ICTYearlyPlanner).map(term => (
              <Button
                key={term}
                variant={selectedTerm === term ? "default" : "outline"}
                onClick={() => setSelectedTerm(term)}
                data-testid={`term-btn-${term}`}
              >
                {year12ICTYearlyPlanner[term].name}
              </Button>
            ))}
          </div>

          {termData && (
            <div className="space-y-6">
              {/* Term Header */}
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{termData.name}</h2>
                      <p className="text-gray-600">{termData.weeks} • Focus: {termData.focus}</p>
                    </div>
                    <Badge variant="outline" className="self-start">
                      {termData.units.length} Units
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Units */}
              <div className="grid gap-4">
                {termData.units.map((unit, idx) => (
                  <Card key={unit.id} className="overflow-hidden">
                    <CardHeader className="bg-gray-50 border-b py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{unit.title}</CardTitle>
                          <CardDescription>
                            {unit.weeks} • {unit.strand} - {unit.subStrand}
                          </CardDescription>
                        </div>
                        <Badge>{unit.assessment || 'No Assessment'}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid md:grid-cols-3 gap-4">
                        {/* Topics */}
                        {unit.topics && (
                          <div>
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                              <BookOpen className="w-4 h-4 text-blue-600" />
                              Topics
                            </h4>
                            <ul className="text-sm space-y-1">
                              {unit.topics.map((topic, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <ChevronRight className="w-3 h-3 mt-1.5 text-gray-400" />
                                  {topic}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Activities */}
                        {unit.activities && (
                          <div>
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                              <Layers className="w-4 h-4 text-green-600" />
                              Activities
                            </h4>
                            <ul className="text-sm space-y-1">
                              {unit.activities.map((activity, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-3 h-3 mt-1.5 text-green-500" />
                                  {activity}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* SLOs Covered */}
                        {unit.slosCovered && (
                          <div>
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4 text-purple-600" />
                              SLOs Covered
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {unit.slosCovered.map((slo, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {slo}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Unit Tests Tab */}
        <TabsContent value="unit-tests" className="mt-6">
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Unit Tests with Answer Keys</h3>
                    <p className="text-sm text-gray-600">Full tests (30-40 marks, 45 mins) for each unit with complete answer keys</p>
                  </div>
                  <Badge className="bg-blue-600">{Object.keys(year12ICTUnitTests).length} Unit Tests</Badge>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {Object.entries(year12ICTUnitTests).map(([key, test]) => (
                <Card key={key} className="overflow-hidden">
                  <CardHeader className="bg-gray-50 border-b py-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div>
                        <CardTitle className="text-base">{test.title}</CardTitle>
                        <CardDescription>
                          Duration: {test.duration} • Total: {test.totalMarks} marks
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => printTest(test)}
                        >
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => toggleAnswers(test.id)}
                        >
                          {showAnswers[test.id] ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                          {showAnswers[test.id] ? 'Hide' : 'Show'} Answers
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleCreateAssessment(test, 'unit')}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Create Assessment
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {/* Test Blueprint */}
                    {year12ICTTestBlueprints[key] && (
                      <div className="bg-amber-50 p-3 rounded-lg mb-4">
                        <h4 className="font-semibold text-sm mb-2">Test Blueprint</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {Object.entries(year12ICTTestBlueprints[key].breakdown).map(([type, data]) => (
                            <div key={type} className="bg-white p-2 rounded">
                              <p className="font-medium capitalize">{type.replace(/([A-Z])/g, ' $1').trim()}</p>
                              <p>{data.count} questions • {data.marks} marks</p>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          <span className="text-xs font-medium">SLOs: </span>
                          {year12ICTTestBlueprints[key].slosCovered?.slice(0, 5).map((slo, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{slo}</Badge>
                          ))}
                          {year12ICTTestBlueprints[key].slosCovered?.length > 5 && (
                            <Badge variant="outline" className="text-xs">+{year12ICTTestBlueprints[key].slosCovered.length - 5} more</Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Questions Preview */}
                    <Accordion type="single" collapsible className="w-full">
                      {test.sections.map((section, idx) => (
                        <AccordionItem key={idx} value={`section-${idx}`}>
                          <AccordionTrigger className="text-sm font-semibold">
                            {section.name} ({section.marks} marks)
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3">
                              {section.questions.slice(0, 5).map((q, qIdx) => (
                                <div key={qIdx} className="p-2 bg-gray-50 rounded text-sm">
                                  <p><strong>Q{q.id}.</strong> {q.question.substring(0, 150)}{q.question.length > 150 ? '...' : ''}</p>
                                  {showAnswers[test.id] && q.answer && (
                                    <p className="mt-1 text-green-600 text-xs"><strong>Answer:</strong> {typeof q.answer === 'string' ? q.answer.substring(0, 100) : q.answer}...</p>
                                  )}
                                </div>
                              ))}
                              {section.questions.length > 5 && (
                                <p className="text-xs text-gray-500 italic">+ {section.questions.length - 5} more questions...</p>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Exercises Tab */}
        <TabsContent value="exercises" className="mt-6">
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Unit Exercises</h3>
                    <p className="text-sm text-gray-600">Practice exercises for every unit to reinforce learning</p>
                  </div>
                  <Badge className="bg-green-600">{Object.keys(year12ICTExercises).length} Units</Badge>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {Object.entries(year12ICTExercises).map(([key, unitExercises]) => (
                <Card key={key} className="overflow-hidden">
                  <CardHeader className="bg-green-50 border-b py-3">
                    <CardTitle className="text-base">{unitExercises.unitTitle}</CardTitle>
                    <CardDescription>{unitExercises.exercises.length} exercises</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {unitExercises.exercises.map((exercise, idx) => (
                        <div key={idx} className="border rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-sm">{exercise.title}</h4>
                            <Badge variant="outline" className="text-xs capitalize">{exercise.type}</Badge>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{exercise.instructions}</p>
                          {exercise.tasks && (
                            <ul className="text-xs space-y-1">
                              {exercise.tasks.slice(0, 3).map((task, tIdx) => (
                                <li key={tIdx} className="flex items-start gap-1">
                                  <CheckCircle2 className="w-3 h-3 mt-0.5 text-green-500 shrink-0" />
                                  {task}
                                </li>
                              ))}
                              {exercise.tasks.length > 3 && (
                                <li className="text-gray-400">+ {exercise.tasks.length - 3} more tasks</li>
                              )}
                            </ul>
                          )}
                          <Button size="sm" variant="outline" className="mt-2 w-full">
                            <Download className="w-3 h-3 mr-1" /> Download Worksheet
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="mt-6">
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-purple-50 to-violet-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Practical Assignments</h3>
                    <p className="text-sm text-gray-600">Major projects for practical units (PC Building, Database, Programming, Web Page)</p>
                  </div>
                  <Badge className="bg-purple-600">{Object.keys(year12ICTAssignments).length} Assignments</Badge>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6">
              {Object.entries(year12ICTAssignments).map(([key, assignment]) => (
                <Card key={key} className="overflow-hidden">
                  <CardHeader className="bg-purple-50 border-b">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                      <div>
                        <Badge className="mb-2">{assignment.type}</Badge>
                        <CardTitle>{assignment.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {assignment.unitTitle} • Duration: {assignment.duration} • Total: {assignment.totalMarks} marks
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" /> Download Brief
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-700 mb-4">{assignment.overview}</p>
                    
                    {/* Objectives */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm mb-2">Learning Objectives:</h4>
                      <ul className="text-sm space-y-1">
                        {assignment.objectives.map((obj, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Target className="w-4 h-4 mt-0.5 text-purple-500 shrink-0" />
                            {obj}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Requirements */}
                    <Accordion type="single" collapsible className="w-full">
                      {Object.entries(assignment.requirements).map(([partKey, part]) => (
                        <AccordionItem key={partKey} value={partKey}>
                          <AccordionTrigger className="text-sm font-semibold">
                            {part.title}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="text-sm space-y-2">
                              {part.tasks.map((task, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                                  {task}
                                </li>
                              ))}
                            </ul>
                            <p className="text-xs text-gray-500 mt-2 italic">Deliverable: {part.deliverable}</p>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>

                    {/* Rubric */}
                    {assignment.rubric && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-sm mb-2">Grading Rubric:</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="p-2 bg-green-100 rounded"><strong>Excellent:</strong> {assignment.rubric.excellent.split(':')[0]}</div>
                          <div className="p-2 bg-blue-100 rounded"><strong>Good:</strong> {assignment.rubric.good.split(':')[0]}</div>
                          <div className="p-2 bg-yellow-100 rounded"><strong>Satisfactory:</strong> {assignment.rubric.satisfactory.split(':')[0]}</div>
                          <div className="p-2 bg-red-100 rounded"><strong>Needs Work:</strong> {assignment.rubric.needsImprovement.split(':')[0]}</div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Term Tests Tab */}
        <TabsContent value="tests" className="mt-6">
          {/* Term Selector */}
          <div className="flex gap-2 mb-6">
            {Object.keys(year12ICTTests).map(term => (
              <Button
                key={term}
                variant={selectedTerm === term ? "default" : "outline"}
                onClick={() => setSelectedTerm(term)}
              >
                {term.replace('term', 'Term ')}
              </Button>
            ))}
          </div>

          <div className="space-y-6">
            {termTests.length > 0 ? termTests.map(test => (
              <Card key={test.id} className="overflow-hidden">
                <CardHeader className="bg-blue-50 border-b">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle>{test.title}</CardTitle>
                      <CardDescription>
                        Duration: {test.duration} • Total Marks: {test.totalMarks}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => printTest(test)}
                      >
                        <Printer className="w-4 h-4 mr-1" /> Print Test
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleAnswers(test.id)}
                      >
                        {showAnswers[test.id] ? (
                          <><EyeOff className="w-4 h-4 mr-1" /> Hide Answers</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-1" /> Show Answers</>
                        )}
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleCreateAssessment(test, selectedTerm)}
                        data-testid={`create-assessment-${test.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Create Assessment
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {/* Instructions */}
                  <div className="bg-amber-50 p-3 rounded-lg mb-4">
                    <h4 className="font-semibold text-sm mb-1">Instructions:</h4>
                    <ul className="text-sm text-gray-700 list-disc list-inside">
                      {test.instructions.map((inst, i) => (
                        <li key={i}>{inst}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Sections */}
                  {test.sections.map((section, sIdx) => (
                    <div key={sIdx} className="mb-6">
                      <h3 className="font-bold text-lg mb-3 pb-2 border-b">
                        {section.name} ({section.marks} marks)
                      </h3>
                      <div className="space-y-4">
                        {section.questions.map((q, qIdx) => (
                          <div key={qIdx} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start gap-2">
                              <span className="font-bold text-gray-700">Q{q.id}.</span>
                              <div className="flex-1">
                                <p className="whitespace-pre-line">{q.question}</p>
                                {q.options && (
                                  <div className="mt-2 space-y-1 ml-4">
                                    {q.options.map((opt, oIdx) => (
                                      <p 
                                        key={oIdx}
                                        className={showAnswers[test.id] && q.answer && opt.startsWith(q.answer) 
                                          ? 'text-green-600 font-semibold' 
                                          : ''}
                                      >
                                        {opt}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                {showAnswers[test.id] && q.answer && (
                                  <p className="mt-2 text-green-600 font-medium">
                                    Answer: {q.answer}
                                  </p>
                                )}
                              </div>
                              <Badge variant="outline" className="shrink-0">
                                {q.marks} mark{q.marks > 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )) : (
              <Card className="p-8 text-center">
                <p className="text-gray-500">No tests available for this term yet.</p>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Worksheets Tab */}
        <TabsContent value="worksheets" className="mt-6">
          <div className="grid gap-4">
            {Object.entries(year12ICTWorksheets).map(([strandKey, worksheets]) => (
              <div key={strandKey}>
                <h3 className="text-lg font-semibold mb-3 capitalize">
                  {strandKey.replace('strand', 'Strand ')} Worksheets
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {worksheets.map(worksheet => (
                    <Card key={worksheet.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{worksheet.title}</CardTitle>
                        <CardDescription>
                          {worksheet.subStrand}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">{worksheet.instructions}</p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {worksheet.slosCovered.map((slo, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {slo}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="flex-1">
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1">
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Textbooks */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Textbooks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {year12ICTResources.textbooks.map((book, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <FileText className="w-4 h-4 mt-0.5 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">{book.title}</p>
                        <p className="text-xs text-gray-500">{book.type}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Online Resources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-green-600" />
                  Online Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {year12ICTResources.onlineResources.map((resource, i) => (
                    <li key={i}>
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-start gap-2 text-blue-600 hover:underline"
                      >
                        <GraduationCap className="w-4 h-4 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">{resource.name}</p>
                          <p className="text-xs text-gray-500">{resource.description}</p>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Software */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-purple-600" />
                  Software
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {year12ICTResources.software.map((sw, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Code className="w-4 h-4 mt-0.5 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">{sw.name}</p>
                        <p className="text-xs text-gray-500">{sw.purpose}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Video Tutorials */}
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-red-600" />
                  Video Tutorials
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {year12ICTResources.videos.map((video, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg text-center">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Layers className="w-6 h-6 text-red-600" />
                      </div>
                      <p className="font-medium text-sm">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.topic} • {video.duration}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
